import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useMemo,
  useState,
} from 'react';
import { PaymentSelectOption } from '@/components';
import { useAppSelector } from '@/redux/hooks';
import { paymentSelectors } from '@/redux/selectors';

export interface PaymentContextProps {
  selectedOption: PaymentSelectOption | null;
  setSelectedOption: Dispatch<SetStateAction<PaymentSelectOption | null>>;
  getBestDiscount: (tariffCost: number, userDiscount: number) => number;
  calculateDiscount: (symbol: string) => number;
  isDiscountWithExpirationTime: () => boolean;
}

export const PaymentContext = createContext<PaymentContextProps | undefined>(
  undefined,
);

type PaymentProviderProps = {
  children: ReactNode;
};

export const PaymentProvider = ({ children }: PaymentProviderProps) => {
  const [selectedOption, setSelectedOption] =
    useState<PaymentSelectOption | null>(null);
  const { paymentPlans, discountDueTime } = useAppSelector(
    paymentSelectors.getPaymentSlice,
  );

  const calculateDiscount = (symbol: string) => {
    return Number(paymentPlans?.assetsDiscounts?.[symbol]) || 0;
  };

  const getBestDiscount = (tariffCost: number, userDiscount: number) => {
    if (!selectedOption) return userDiscount;
    // Don't use method discount if tariff cost less than a min method deposit amount
    if (paymentPlans!.assetsMinAmounts?.[selectedOption.value] > tariffCost)
      return userDiscount;

    const methodDiscount = calculateDiscount(selectedOption!.value);

    if (userDiscount > methodDiscount) {
      return userDiscount;
    }
    return methodDiscount;
  };
  const isDiscountWithExpirationTime = () => {
    return discountDueTime
      ? discountDueTime > Math.round(new Date().getTime() / 1000)
      : false;
  };

  const value = useMemo(
    () => ({
      selectedOption,
      setSelectedOption,
      getBestDiscount,
      calculateDiscount,
      isDiscountWithExpirationTime,
    }),
    [selectedOption, setSelectedOption, paymentPlans, discountDueTime],
  );

  return (
    <PaymentContext.Provider value={value}>{children}</PaymentContext.Provider>
  );
};
